/**
 * AI studio: try a prompt against arbitrary text, against a real newsletter, or
 * against an uploaded PDF or photo — without touching any record. Useful for
 * tuning a prompt before saving it, and for one-off extractions.
 *
 * A file is read on the server and the bytes are dropped; only the text and the
 * pictures come back. How a PDF is read is the user's choice, because the cheap
 * answer is usually the right one: a PDF that carries a text layer needs no model
 * at all, and a model would only add a chance of being wrong.
 */
import { useRef, useState } from 'react'
import { PageHeader } from '../App'
import { api, useApi, qs, reportClientError } from '../lib/api'
import { HEIC_ACCEPT, isHeic } from '../lib/heic'
import { PromptSelect, ModelSelect, PartnerSelect, EnumSelect } from '../components/Select'
import { PROMPT_FIELDS } from '../lib/promptFields'
import { PDF_MODES, pdfMode as pdfModeInfo } from '../lib/pdfModes'
import { ExtractionEngines } from '../components/Engines'
import { Card, Field, Badge, Notice, Spinner, Empty, Filters, useToast, fmtInt, fmtBytes } from '../components/ui'
import { Icon } from '../components/Icon'
import { Dropzone } from '../components/Dropzone'

const stripHtml = (h) =>
  String(h || '')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*p\s*>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .trim()

// The prompt classifications, minus image alt text — a caption belongs to a stored
// picture, and there is no picture here to hang one on. Everything else is offered,
// including the transcription mapping, so a prompt written for reading a photo or a
// scan can be tried against the model that will actually run it. The list is shared
// with the prompts page (lib/promptFields.js) so the studio cannot drift out of step
// with the classifications an admin files prompts under, which is how it came to be
// missing one of them.
const FIELDS = PROMPT_FIELDS.filter((f) => f.value !== 'media.alt_text').map((f) => ({
  value: f.value,
  label: f.label,
}))

const PDF_MIME = 'application/pdf'
const IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const ACCEPT = [PDF_MIME, ...IMAGE_MIME, HEIC_ACCEPT].join(',')

/** What came back from the server, whether from a prompt run or a file read. */
const fromRun = (res) => ({
  output: res.output,
  model: res.model,
  prompt: res.prompt,
  ms: res.ms,
  pictures: [],
  picturesSkipped: [],
  source: null,
  // No file, so nothing was extracted — the model that ran is named in the card
  // heading, where a prompt run has always reported it.
  engines: null,
})

const fromImport = (res) => ({
  output: res.text,
  model: res.model,
  prompt: res.prompt,
  ms: res.ms,
  pictures: res.pictures ?? [],
  picturesSkipped: res.pictures_skipped ?? [],
  source: res.text_source,
  error: res.text_error,
  pages: res.pages,
  engines: res.engines ?? null,
})

const SOURCE_LABEL = {
  'text-layer': 'PDF text layer — no model',
  'ai-convert': 'AI document conversion',
  'vision-ocr': 'vision OCR',
  vision: 'vision model',
}

export default function AiStudio({ user }) {
  const toast = useToast()
  const fileRef = useRef(null)
  const [fieldKey, setFieldKey] = useState('newsletters.body_html')
  const [promptId, setPromptId] = useState(null)
  const [modelId, setModelId] = useState(null)
  const [instruction, setInstruction] = useState('')
  const [input, setInput] = useState('')
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [partnerId, setPartnerId] = useState(null)
  const [file, setFile] = useState(null)
  const [pdfMode, setPdfMode] = useState('layer')

  const resolved = useApi(['ai-resolve', fieldKey], `ai/resolve${qs({ field_key: fieldKey })}`)

  const isPdf = file?.type === PDF_MIME
  // A picture can only be read by a model that can see, and so can a PDF the user
  // has asked to OCR; the override list has to follow, or it offers models that
  // will be rejected the moment Run is pressed.
  const needsVision = !!file && (!isPdf || pdfMode === 'vision')
  const maxBytes = user.maxImportBytes ?? 20971520

  // Pull a real newsletter in as sample input.
  const sample = useApi(
    ['studio-sample', partnerId],
    `newsletters${qs({ partner_id: partnerId, limit: 5, sort: 'published_on', dir: 'desc' })}`,
    { enabled: !!partnerId }
  )

  const loadSample = async (id) => {
    try {
      const d = await api(`newsletters/${id}`)
      setInput(
        fieldKey === 'newsletters.prayer_requests'
          ? (d.newsletter.prayer_requests ?? '')
          : stripHtml(d.newsletter.body_html)
      )
      toast.info('Loaded into the input box')
    } catch (err) {
      toast.error(err)
    }
  }

  const pickFile = (f) => {
    setError(null)
    if (!f) return
    if (f.type !== PDF_MIME && !IMAGE_MIME.includes(f.type) && !isHeic(f)) {
      setError(`${f.type || 'That file type'} cannot be read. Use a PDF, or a JPEG, PNG, WebP, GIF or HEIC image.`)
      return
    }
    if (f.size > maxBytes) {
      setError(`${f.name} is ${fmtBytes(f.size)}; the limit is ${fmtBytes(maxBytes)}.`)
      return
    }
    setFile(f)
    setResult(null)
    setModelId(null)
  }

  const clearFile = () => {
    setFile(null)
    setModelId(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  /** Post the attached file, optionally asking for the chosen prompt to run on it. */
  const sendFile = async ({ withPrompt }) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('entity', 'studio')
    fd.append('field_key', fieldKey)
    if (isPdf) fd.append('pdf_mode', pdfMode)
    // Without a prompt this is a plain read, so the only model that may be named is
    // the one a scan needs to be OCR'd with at all.
    if (modelId && (withPrompt || needsVision)) {
      fd.append(needsVision ? 'vision_model_id' : 'model_id', String(modelId))
    }
    if (withPrompt) {
      if (promptId) fd.append('prompt_id', String(promptId))
      else if (instruction.trim()) fd.append('instruction', instruction.trim())
      if (isPdf) fd.append('tidy', '1')
    }
    return fromImport(await api('ai/import', { method: 'POST', formData: fd }))
  }

  /**
   * Read the file into the input box, so the extracted text can then be worked on
   * like anything else that was pasted there. For a PDF this is the no-model path.
   */
  const extract = async () => {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      const res = await sendFile({ withPrompt: false })
      setResult(res)
      if (res.output) {
        setInput(res.output)
        toast.ok(`${fmtInt(res.output.length)} characters into the input box`)
      } else {
        toast.info(res.pictures.length ? 'No text — but there are pictures below' : 'Nothing could be read')
      }
    } catch (err) {
      setError(err.message)
      reportClientError('import', err, {
        filename: file?.name,
        mime_type: file?.type,
        bytes: file?.size,
        entity: 'studio',
        context: { pdf_mode: isPdf ? pdfMode : null },
      })
    } finally {
      setBusy(false)
    }
  }

  const run = async () => {
    if (!file && !input.trim()) {
      setError('Paste some text, load an update, or attach a file first.')
      return
    }
    if (!promptId && !instruction.trim()) {
      setError('Pick a saved prompt or write an instruction.')
      return
    }
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setResult(
        file
          ? await sendFile({ withPrompt: true })
          : fromRun(
              await api('ai/run', {
                method: 'POST',
                body: {
                  prompt_id: promptId,
                  instruction: promptId ? undefined : instruction,
                  input,
                  field_key: fieldKey,
                  model_id: modelId,
                  entity: 'studio',
                },
              })
            )
      )
    } catch (err) {
      setError(err.message)
      if (file) {
        reportClientError('import', err, {
          filename: file.name,
          mime_type: file.type,
          bytes: file.size,
          entity: 'studio',
        })
      }
    } finally {
      setBusy(false)
    }
  }

  // "oversize" is not an encoding failure — it decoded fine and was simply too big —
  // so it reads differently from a picture stored in a filter this importer can't
  // lift out at all.
  const skippedReasons = result?.picturesSkipped ?? []
  const oversizeCount = skippedReasons.filter((r) => r === 'oversize').length
  const encodingReasons = [...new Set(skippedReasons.filter((r) => r !== 'oversize'))]
  const encodingCount = skippedReasons.length - oversizeCount

  if (!user.caps.useAi) {
    return (
      <PageHeader title="AI Studio">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          Your role ({user.roleLabel}) cannot run AI. Ask an owner for editor access or higher.
        </Notice>
      </PageHeader>
    )
  }

  return (
    <PageHeader
      title="AI Studio"
      sub="Test a prompt against real text or a file — nothing is written to any record"
      actions={
        resolved.data?.ok ? (
          <Badge tone={resolved.data.model.enabled ? 'ok' : 'warn'} dot>
            {resolved.data.model.label}
          </Badge>
        ) : null
      }
    >
      <div className="col">
        {resolved.data && !resolved.data.ok && (
          <Notice tone="warn" icon="warning" title="No model is mapped to this field">
            {resolved.data.error}
          </Notice>
        )}
        {resolved.data?.ok && !resolved.data.model.enabled && (
          <Notice tone="warn" icon="warning" title={`${resolved.data.model.label} is disabled`}>
            Enable it on AI Settings → AI Models, or pick a different model below.
          </Notice>
        )}

        {/* Open by default, unlike a filter panel: these are the page's controls, and
            there is nothing to look at until they are set. */}
        <Filters id="ai-studio" label="Run settings" defaultOpen>
          <Field label="Field" hint="Decides which prompts and which mapped model apply">
            <EnumSelect
              value={fieldKey}
              onChange={(v) => {
                setFieldKey(v || 'newsletters.body_html')
                setPromptId(null)
                setResult(null)
              }}
              options={FIELDS}
              isClearable={false}
            />
          </Field>
          <Field label="Saved prompt" hint="Or leave empty and write an instruction below">
            <PromptSelect value={promptId} onChange={setPromptId} targetField={fieldKey} placeholder="None — use my instruction" />
          </Field>
          <Field
            label="Model / Model override"
            hint={
              needsVision
                ? 'A picture needs a model that can see'
                : resolved.data?.ok
                  ? `Runs on ${resolved.data.model.label} unless you pick one below.`
                  : 'Leave empty to use the mapped model'
            }
          >
            <ModelSelect
              value={modelId}
              onChange={setModelId}
              modality={needsVision ? 'vision' : 'text'}
              placeholder={
                !needsVision && resolved.data?.ok
                  ? `${resolved.data.model.label} (field default)`
                  : 'Use the mapped default'
              }
            />
          </Field>
          <Field label="Load a sample from" hint="Pulls a real update into the input box">
            <PartnerSelect value={partnerId} onChange={setPartnerId} withNewsletters placeholder="Pick a partner unit…" />
          </Field>
        </Filters>

        {partnerId && sample.data?.rows?.length ? (
          <Card title="Recent Updates from This Partner Unit" sub="Click to load one into the input">
            <div className="chip-row">
              {sample.data.rows.map((r) => (
                <button key={r.id} className="btn btn-sm" onClick={() => loadSample(r.id)}>
                  {r.published_on} · {fmtInt(r.word_count)}w
                </button>
              ))}
            </div>
          </Card>
        ) : null}

        {!promptId && (
          <Card title="Instruction">
            <textarea
              className="textarea"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder={
                file
                  ? 'e.g. List every place name mentioned in this document.'
                  : 'e.g. Rewrite this as three bullet points suitable for a prayer bulletin.'
              }
              rows={4}
            />
          </Card>
        )}

        <div className="grid grid-2">
          <Card
            title="Input"
            sub={file ? `${file.name} · ${fmtBytes(file.size)}` : `${fmtInt(input.length)} characters`}
            actions={
              <>
                <button className="btn btn-sm" onClick={() => fileRef.current?.click()} disabled={busy}>
                  <Icon name="imports" size={14} /> {file ? 'Change file' : 'Attach a file'}
                </button>
                <button className="btn btn-sm" onClick={() => setInput('')} disabled={!input}>
                  Clear text
                </button>
              </>
            }
          >
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => pickFile(e.target.files?.[0])}
            />

            {file ? (
              <div className="col">
                <div className="file-chip">
                  <Icon name={isPdf ? 'file' : 'image'} size={22} />
                  <div className="col-tight truncate">
                    <strong className="truncate">{file.name}</strong>
                    <span className="small subtle">
                      {fmtBytes(file.size)} · read on the server, never stored
                    </span>
                  </div>
                  <span className="spacer" />
                  <button className="btn btn-sm" onClick={clearFile} disabled={busy}>
                    Remove
                  </button>
                </div>

                {isPdf ? (
                  <Field label="How to read this PDF" hint={pdfModeInfo(pdfMode)?.note}>
                    <div className="btn-group">
                      {PDF_MODES.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          className={`btn${pdfMode === o.value ? ' active' : ''}`}
                          onClick={() => {
                            setPdfMode(o.value)
                            setModelId(null)
                          }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </Field>
                ) : (
                  <Notice tone="warn" icon="eye" title="A model is reading this picture">
                    There is no text in a photograph to extract, only a model’s reading of it.
                    Proofread anything it hands back — names and numbers are where these models slip.
                  </Notice>
                )}

                <div className="row">
                  <button className="btn btn-sm" onClick={extract} disabled={busy}>
                    {busy ? <Spinner /> : isPdf ? 'Extract text and pictures' : 'Transcribe into the input box'}
                  </button>
                  <span className="small subtle">
                    {!isPdf
                      ? 'Uses the built-in transcriber, then you can run prompts on the text.'
                      : pdfMode === 'layer'
                        ? 'Reads the text layer and lifts out the embedded pictures — no prompt, no model.'
                        : 'Reads the file the way chosen above and lifts out the pictures, with no prompt on top.'}
                  </span>
                </div>
              </div>
            ) : (
              <Dropzone
                compact
                onFiles={(files) => pickFile(files[0])}
                onBrowse={() => fileRef.current?.click()}
                busy={busy}
                title="Drop a PDF or picture here to run the prompt on it, or type below"
              >
                PDF, JPEG, PNG, WebP, GIF or HEIC up to {fmtBytes(maxBytes)}
              </Dropzone>
            )}

            {/* Kept visible with a file attached: extraction lands its text here, and
                seeing it is the point — it is what a prompt would be run against once
                the file is removed. */}
            <textarea
              className="textarea textarea-lg"
              style={{ marginTop: 'var(--sp-3)' }}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                file ? 'Extracted text appears here…' : 'Paste text here, or load an update above…'
              }
            />
            {file && (
              <div className="small subtle" style={{ marginTop: 6 }}>
                While a file is attached the prompt runs on the file. Remove it to run on
                this text instead.
              </div>
            )}
          </Card>

          <Card
            title="Result"
            sub={
              result
                ? [
                    result.source ? (SOURCE_LABEL[result.source] ?? result.source) : null,
                    result.model?.label,
                    `${result.ms} ms`,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : undefined
            }
            actions={
              result?.output ? (
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    navigator.clipboard?.writeText(result.output)
                    toast.ok('Copied')
                  }}
                >
                  Copy
                </button>
              ) : null
            }
          >
            {error && (
              <Notice tone="danger" icon="warning">
                {error}
              </Notice>
            )}
            {!result && !error && !busy && <Empty icon="ai" title="Run a prompt to see the output" />}
            {busy && <Spinner label="Running…" />}
            {result && (
              <div className="col">
                {result.error && (
                  <Notice tone="warn" icon="warning" title="The text could not be read">
                    {result.error}
                  </Notice>
                )}
                {result.output ? (
                  <div className="diff-pane diff-new">
                    <div className="diff-head">
                      {result.prompt?.name ?? (result.source ? 'as extracted' : 'ad-hoc instruction')}
                      <span className="spacer" />
                      <span style={{ textTransform: 'none', letterSpacing: 0 }}>
                        {fmtInt(result.output.length)} chars
                        {result.pages ? ` · ${result.pages} page${result.pages === 1 ? '' : 's'}` : ''}
                      </span>
                    </div>
                    <div className="diff-body">{result.output}</div>
                  </div>
                ) : (
                  <Empty icon="empty" title="No text came back" />
                )}

                <ExtractionEngines engines={result.engines} />

                {result.pictures.length ? (
                  <Field
                    label={`Pictures in this file — ${result.pictures.length}`}
                    hint="Lifted out of the file as they are. Right-click to save one; nothing is stored here."
                  >
                    <div className="media-grid">
                      {result.pictures.map((p, i) => (
                        <a
                          className="media-item"
                          key={i}
                          href={`data:${p.mime_type};base64,${p.data}`}
                          download={`picture-${i + 1}.${p.mime_type === 'image/png' ? 'png' : 'jpg'}`}
                          title="Download"
                        >
                          <img className="media-thumb" src={`data:${p.mime_type};base64,${p.data}`} alt={`Picture ${i + 1}`} />
                          <div className="media-meta subtle truncate">
                            {p.width && p.height ? `${p.width}×${p.height} · ` : ''}
                            {fmtBytes(p.bytes)}
                          </div>
                        </a>
                      ))}
                    </div>
                  </Field>
                ) : null}

                {skippedReasons.length ? (
                  <div className="small subtle">
                    {encodingCount ? (
                      <>
                        {encodingCount} picture{encodingCount === 1 ? '' : 's'} could not be read (
                        {encodingReasons.join(', ')}).{' '}
                      </>
                    ) : null}
                    {oversizeCount ? (
                      <>
                        {oversizeCount} picture{oversizeCount === 1 ? '' : 's'} {oversizeCount === 1 ? 'was' : 'were'}{' '}
                        too large to import.
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </Card>
        </div>

        <div className="row">
          <button className="btn btn-primary btn-lg" onClick={run} disabled={busy}>
            {busy ? <Spinner /> : file ? `Run prompt on the ${isPdf ? 'PDF' : 'picture'}` : 'Run prompt'}
          </button>
          <span className="small subtle">
            {file && isPdf && pdfMode === 'layer'
              ? 'The text layer is read here, then the prompt runs on it. Every run is recorded on the AI Usage report.'
              : 'Every run is recorded on the AI Usage report with the model and duration.'}
          </span>
        </div>
      </div>
    </PageHeader>
  )
}
